import { useState } from 'react'
import { useMutation, useQuery } from '@w/react-query'
import { queryOptions } from '@w/react-query/db'
import { s } from '@w/schema'
import { unknownErrorToError } from '@w/utils'
import { Button } from '@w/ui/components/button'
import { IconButton } from '@w/ui/components/icon-button'
import { ChevronLeftIcon, FileTextIcon, RotateCcwIcon, Trash2Icon } from '@w/ui/components/icon'
import { Dialog, DialogPopup, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@w/ui/components/dialog'
import { ErrorDetails } from '@w/ui/components/error-details'
import { useNavigate, useParams, useRouterContext } from '../router/flashcards-fe.router-bindings'
import { useUserSpaces } from '../router/authenticated-layout'
import { contentResources, useSpacesView } from '../db/spaces-queries'
import type { SpaceResource } from '../router/spaces-resources'
import { changeTrashedPage } from './trash-commands'

const trashRow = s.object({ id: s.string(), title: s.string(), deletedAt: s.number(), localLevel: s.number() })
type TrashRow = ReturnType<typeof trashRow.parse>

export function TrashPage() {
  const { workspaceId } = useParams({ from: '/$workspaceId/trash' })
  const ctx = useRouterContext()
  const navigate = useNavigate()
  const spaces = useUserSpaces()
  const view = useSpacesView(spaces, workspaceId, ctx.queryClient)
  const resource = view.data ? contentResources(view.data)[0] : undefined
  return <main className="bg-surface-1 text-on-surface flex min-h-dvh flex-col gap-6 p-4 md:p-6" data-testid="flashcards-trash">
    <header className="flex items-center gap-3">
      <IconButton aria-label="Back to pages" semanticRole="secondary" variant="text" onClick={() => { navigate({ to: '/' }) }}><ChevronLeftIcon />Back to pages</IconButton>
      <h1 className="text-xl font-semibold">Trash</h1>
    </header>
    {view.error ? <ErrorDetails error={unknownErrorToError(view.error)} /> : null}
    {resource ? <TrashContents key={workspaceId} resource={resource} /> : null}
  </main>
}

function TrashContents({ resource }: { resource: SpaceResource }) {
  const ctx = useRouterContext()
  const workspaceId = resource.space.workspaceId
  const db = resource.db
  const pages = useQuery(queryOptions(db.query`SELECT id, title, deletedAt, COALESCE((SELECT MAX(level) FROM pagePermissions g WHERE g.pageId=pages.id AND g.principalKind='user' AND g.principalId=${resource.userId}),0) AS localLevel FROM pages
    WHERE deletedAt IS NOT NULL AND kind != 'databaseRow'
      AND kind<>'personalRoot'
      AND EXISTS (SELECT 1 FROM pageRelations r WHERE r.toPageId=pages.id AND r.type='child')
    ORDER BY deletedAt DESC, id`, trashRow), resource.queryClient)
  const capabilities = useQuery(ctx.rpc.page.levels.queryOptions({ workspaceId }), ctx.queryClient)
  const [confirming, setConfirming] = useState<TrashRow | null>(null)
  const command = useMutation({
    mutationFn: (input: { pageId: string; action: 'restore' | 'purge' }) => changeTrashedPage(ctx, { ...input, spaceId: resource.space.id, level: capabilities.data?.levels[input.pageId] ?? pages.data?.find(page => page.id === input.pageId)?.localLevel ?? 0 }),
    onSuccess: () => { setConfirming(null) },
  })
  const visiblePages = pages.data?.filter(page => (capabilities.data?.levels[page.id] ?? page.localLevel) >= 1)
  const error = (confirming ? undefined : command.error) ?? pages.error ?? capabilities.error
  return <section className="flex w-full max-w-3xl flex-col gap-4" aria-label="Deleted pages">
    <p className="text-on-surface-secondary text-sm">Restore deleted pages or permanently delete them. Move or permanently delete child pages before deleting their parent.</p>
    {visiblePages?.length === 0 && capabilities.data && !capabilities.error ? <p data-testid="flashcards-trash-empty">Trash is empty.</p> : null}
    <ul className="flex flex-col gap-2">
      {visiblePages?.map(page => {
        const level = capabilities.data?.levels[page.id] ?? page.localLevel
        return <li key={page.id} data-testid={`trash-page-${page.id}`} className="border-separator flex flex-wrap items-center gap-3 rounded-lg border p-3">
          <span className="flex min-w-0 flex-1 items-center gap-3">
            <FileTextIcon aria-hidden className="size-4 shrink-0" data-testid={`trash-page-icon-${page.id}`} />
            <span className="truncate">{page.title.trim() || 'Untitled page'}</span>
          </span>
          <div className="flex items-center gap-2">
            {level >= 2 ? <Button data-testid={`trash-restore-${page.id}`} size="sm" variant="text" disabled={command.isPending} onClick={() => { command.mutate({ pageId: page.id, action: 'restore' }) }}><RotateCcwIcon />Restore</Button> : null}
            {level >= 3 ? <Button data-testid={`trash-purge-${page.id}`} size="sm" variant="text" semanticRole="error" disabled={command.isPending} onClick={() => { command.reset(); setConfirming(page) }}><Trash2Icon />Delete</Button> : null}
          </div>
        </li>
      })}
    </ul>
    {error ? <div role="alert"><p>Unable to update Trash</p><ErrorDetails error={unknownErrorToError(error)} compact /></div> : null}
    <Dialog open={confirming !== null} onOpenChange={open => { if (!open && !command.isPending) { command.reset(); setConfirming(null) } }}>
      <DialogPopup data-testid="trash-confirmation">
        <DialogHeader>
          <DialogTitle>Delete?</DialogTitle>
          <DialogDescription>“{confirming ? confirming.title.trim() || 'Untitled page' : 'Untitled page'}” will be permanently deleted. You cannot restore this page.</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button data-testid="trash-purge-cancel" variant="text" disabled={command.isPending} onClick={() => { command.reset(); setConfirming(null) }}>Cancel</Button>
          <Button data-testid="trash-purge-confirm" semanticRole="error" disabled={command.isPending || !confirming} onClick={() => { if (confirming) command.mutate({ pageId: confirming.id, action: 'purge' }) }}><Trash2Icon />Delete</Button>
        </DialogFooter>
        {command.error ? <div role="alert" className="px-6 pb-6"><ErrorDetails error={unknownErrorToError(command.error)} compact /></div> : null}
      </DialogPopup>
    </Dialog>
  </section>
}
